import { ApiError, newIdempotencyKey, request, storedSite, sessionHeaders } from '../../api';

export interface TruthRow {
  readonly entity: string;
  readonly owner: 'platform' | 'legacy';
  readonly comment: string;
}

export interface InboundState {
  readonly entity: string;
  readonly cursor: string | null;
  readonly lastOkAt: string | null;
  readonly lastError: string | null;
}

export interface BridgeStatus {
  readonly configured: boolean;
  readonly inbound: readonly InboundState[];
  readonly adapter: string;
  readonly available: boolean;
  readonly readOnly: boolean;
  readonly error: string | null;
  readonly queueDepth: number;
  readonly failed: number;
  readonly openDiscrepancies: number;
  readonly lastOkAt: string | null;
  readonly truth: readonly { readonly entity: string; readonly owner: string }[];
}

export interface DiscrepancyRow {
  readonly id: string;
  readonly entity: string;
  readonly entityId: string | null;
  readonly side: 'platform' | 'legacy' | 'both';
  readonly kind: string;
  readonly status: 'open' | 'resolved';
  readonly createdAt: string;
}

export interface Checksum {
  readonly rows: number;
  readonly totalMinor: number;
  readonly digest: string;
}

export interface RunRow {
  readonly id: string;
  readonly kind: 'customers' | 'balances' | 'passes' | 'feedback' | 'history' | 'catalog';
  readonly source: string;
  readonly status:
    | 'draft'
    | 'staged'
    | 'dry_run'
    | 'validated'
    | 'committed'
    | 'rolled_back'
    | 'failed';
  readonly checksumBefore: Checksum | null;
  readonly checksumAfter: Checksum | null;
  readonly report: Record<string, unknown>;
  readonly startedAt: string;
}

export interface AccountingTotals {
  readonly orders: number;
  readonly totalMinor: number;
  readonly paidMinor: number;
  readonly refundedMinor: number;
  readonly netMinor: number;
  readonly subscriptionFeesMinor: number;
  readonly subscriptionRefundedMinor: number;
  readonly realizedMinor: number;
}

export interface AccountingExport {
  readonly from: string;
  readonly to: string;
  readonly totals: AccountingTotals;
}

export function fetchStatus(): Promise<BridgeStatus> {
  return request<BridgeStatus>('GET', '/api/v1/bridge/status');
}

export function fetchTruth(): Promise<readonly TruthRow[]> {
  return request<readonly TruthRow[]>('GET', '/api/v1/bridge/truth');
}

export function resync(): Promise<{ readonly sent: number; readonly offline: boolean }> {
  return request('POST', '/api/v1/bridge/resync');
}

export function fetchDiscrepancies(status?: 'open' | 'resolved'): Promise<readonly DiscrepancyRow[]> {
  const query = status ? `?status=${status}` : '';
  return request<readonly DiscrepancyRow[]>('GET', `/api/v1/bridge/discrepancies${query}`);
}

export function resolveDiscrepancy(id: string): Promise<DiscrepancyRow> {
  return request<DiscrepancyRow>('POST', '/api/v1/bridge/discrepancies/resolve', { id });
}

export function fetchRuns(): Promise<readonly RunRow[]> {
  return request<readonly RunRow[]>('GET', '/api/v1/bridge/migration/runs');
}

export function fetchRun(runId: string): Promise<RunRow> {
  return request<RunRow>('GET', `/api/v1/bridge/migration/report/${encodeURIComponent(runId)}`);
}

export function startDryRun(body: {
  readonly kind: RunRow['kind'];
  readonly adapter: string;
  readonly source?: Record<string, string>;
}): Promise<unknown> {
  return request('POST', '/api/v1/bridge/migration/dry-run', body);
}

export function validateRun(runId: string): Promise<{ readonly ok: boolean; readonly checked: number }> {
  return request('POST', '/api/v1/bridge/migration/validate', { runId });
}

export function commitRun(runId: string): Promise<{ readonly written: number }> {
  // Коммит переноса необратим: сервер требует ключ идемпотентности, чтобы
  // повторное нажатие не выполнило перенос дважды.
  return request('POST', '/api/v1/bridge/migration/commit', { runId }, {
    idempotencyKey: newIdempotencyKey('migration-commit'),
  });
}

export function rollbackRun(runId: string): Promise<{ readonly compensated: number }> {
  return request('POST', '/api/v1/bridge/migration/rollback', { runId }, {
    idempotencyKey: newIdempotencyKey('migration-rollback'),
  });
}

export function fetchAccounting(from: string, to: string): Promise<AccountingExport> {
  const params = new URLSearchParams({ from, to });
  return request<AccountingExport>('GET', `/api/v1/bridge/export/accounting?${params.toString()}`);
}

/**
 * Исходящая очередь к старой системе.
 *
 * Плитки «в очереди» и «с ошибкой» на вкладке состояния показывали числа, а
 * посмотреть, какая именно запись застряла и с какой ошибкой, было нельзя:
 * маршрут есть, экрана не было. Разбор «почему клиент не доехал до старой
 * системы» шёл запросом в базу.
 */
export interface OutboundRow {
  readonly id: string;
  readonly entity: string;
  readonly entityId: string;
  readonly op: string;
  readonly payload: unknown;
  readonly status: string;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly deliveryRef: string | null;
}

export function fetchOutbound(status?: string): Promise<readonly OutboundRow[]> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<readonly OutboundRow[]>('GET', `/api/v1/bridge/outbound${suffix}`);
}

export function pullInboundNow(): Promise<unknown> {
  return request('POST', '/api/v1/bridge/inbound/pull');
}

export interface PlanRow {
  readonly sourceRef: string;
  readonly disposition: 'transfer' | 'zero' | 'written_off' | 'needs_decision';
  readonly amountMinor: number;
  readonly customerId: string | null;
  readonly createProfile: boolean;
  readonly reason: string | null;
  readonly phone: string | null;
  readonly amountRaw: string | null;
}

export interface PlanReport {
  readonly summary: {
    readonly stagedRows: number;
    readonly stagedTotalMinor: number;
    readonly transferRows: number;
    readonly transferMinor: number;
    readonly createProfiles: number;
    readonly writtenOffRows: number;
    readonly writtenOffMinor: number;
    readonly needsDecision: number;
  };
  readonly rows: readonly PlanRow[];
}

export function fetchPlan(runId: string): Promise<PlanReport> {
  return request('GET', `/api/v1/bridge/migration/plan/${encodeURIComponent(runId)}`);
}

export function resolveRow(body: {
  readonly runId: string;
  readonly sourceRef: string;
  readonly action: 'assign' | 'write_off';
  readonly customerId?: string;
  readonly amountMinor?: number;
  readonly comment?: string;
}): Promise<PlanRow> {
  return request('POST', '/api/v1/bridge/migration/balances/resolve', body);
}

/**
 * Файл с сервера как есть (CSV, XML): request() разбирает JSON-обёртку, а
 * выгрузки в бухгалтерию и открытый формат отдаются файлом.
 */
export async function requestFile(
  path: string,
): Promise<{ readonly text: string; readonly filename: string; readonly type: string; readonly next: string | null }> {
  const headers: Record<string, string> = {};
  Object.assign(headers, sessionHeaders());
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;
  const res = await fetch(path, { headers });
  const text = await res.text();
  if (!res.ok) {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      payload = {};
    }
    throw new ApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export';
  return {
    text,
    filename,
    type: res.headers.get('content-type') ?? 'text/plain',
    next: res.headers.get('x-moika-next'),
  };
}

export function accountingFile(from: string, to: string, format: 'csv' | '1c-csv' | 'commerceml') {
  const params = new URLSearchParams({ from, to, format });
  return requestFile(`/api/v1/bridge/export/accounting?${params.toString()}`);
}

export const OPEN_ENTITIES = [
  'customers',
  'vehicles',
  'visits',
  'orders',
  'payments',
  'passes',
  'balances',
  'feedback',
  'catalog',
] as const;

/**
 * Выгрузка отдаётся страницами (R-Q8): файл собирается из всех страниц по
 * курсору x-moika-next, чтобы скачанное было полным, а не первыми 100 000 строк.
 */
export async function openExportFile(entity: string, format: 'csv' | 'json') {
  const base = `/api/v1/bridge/export/open/${encodeURIComponent(entity)}?format=${format}`;
  let page = await requestFile(base);
  const parts: string[] = [page.text];
  const rows: unknown[] = format === 'json' ? (JSON.parse(page.text) as unknown[]) : [];
  while (page.next) {
    page = await requestFile(`${base}&cursor=${encodeURIComponent(page.next)}`);
    if (format === 'json') rows.push(...(JSON.parse(page.text) as unknown[]));
    // Заголовок CSV — только у первой страницы.
    else parts.push(page.text.slice(page.text.indexOf('\n') + 1));
  }
  const text = format === 'json' ? JSON.stringify(rows) : parts.filter((p) => p.length > 0).join('\n');
  return { ...page, text, next: null };
}
